import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type Campaign, type CampaignInput, type DriverTier } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column, type BadgeTone } from '@/components/ui';
import { IconAward } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

const PAGE = 25;

type Tab = 'campaigns' | 'tiers';

/** Incentives & Loyalty — the incentives-svc campaigns (driver quests) and the
 *  loyalty-svc driver tier leaderboard.
 *
 *  Campaigns are READ + AUTHORED here: staff with incentives.manage can create
 *  and edit a quest (admin-svc proxies the write to incentives-svc, which owns
 *  the `incentive_campaigns` table; audited + RBAC-gated server-side). Tiers
 *  remain read-only. Each section is DEFENSIVE (shows a "not yet available"
 *  state when its table is absent). Covers the Filament incentive/loyalty
 *  resources (list/view + create/edit). */
export function Incentives() {
  const [tab, setTab] = useState<Tab>('campaigns');
  return (
    <AdminPage>
      <AdminPageHeader title="Incentives & Loyalty" subtitle="Driver quests (campaigns) and loyalty tiers." />
      <div className="mb-4 flex gap-1 rounded-lg border border-ink-800 bg-ink-850 p-1 w-fit">
        <TabButton active={tab === 'campaigns'} onClick={() => setTab('campaigns')} label="Campaigns" />
        <TabButton active={tab === 'tiers'} onClick={() => setTab('tiers')} label="Driver tiers" />
      </div>
      {tab === 'campaigns' ? <CampaignsTable /> : <TiersTable />}
    </AdminPage>
  );
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
        active ? 'bg-brand-600/15 text-brand-400' : 'text-muted hover:bg-ink-800 hover:text-slate-200'
      }`}
    >
      {label}
    </button>
  );
}

/** Driver quests (incentive_campaigns). Read + (for incentives.manage) author. */
function CampaignsTable() {
  const [offset, setOffset] = useState(0);
  // null = closed; { id:0 } = create; { id:>0,… } = edit that campaign.
  const [editing, setEditing] = useState<Campaign | 'new' | null>(null);
  const canManage = useAdminPermission('incentives.manage');

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'campaigns', offset],
    queryFn: ({ signal }) => adminApi.campaigns({ limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<Campaign>> = [
    { key: 'code', header: 'Code', cell: (r) => <span className="metric text-xs text-slate-300">{r.code}</span> },
    { key: 'title', header: 'Title', cell: (r) => <span className="font-medium text-slate-100">{r.title}</span> },
    { key: 'target', header: 'Target', align: 'right', cell: (r) => <span className="metric text-xs text-slate-300">{r.target_count} trips</span> },
    { key: 'reward', header: 'Reward', align: 'right', cell: (r) => <span className="metric text-sm text-ok">${r.reward_amount.toFixed(2)}</span> },
    { key: 'window', header: 'Window', cell: (r) => <span className="text-xs text-muted">{dateTime(r.window_start)} → {dateTime(r.window_end)}</span> },
    { key: 'active', header: 'Active', cell: (r) => <Badge tone={r.active ? 'ok' : 'neutral'} dot>{r.active ? 'active' : 'inactive'}</Badge> },
  ];
  if (canManage) {
    columns.push({
      key: 'edit',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setEditing(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          Edit
        </button>
      ),
    });
  }

  const unavailable = data && data.available === false;
  if (unavailable) {
    return (
      <Card>
        <EmptyState
          title="Campaigns not yet in Postgres"
          description={data?.note ?? 'The incentive_campaigns table is owned by incentives-svc and is not in this Postgres yet.'}
          icon={<IconAward className="h-6 w-6" />}
        />
      </Card>
    );
  }
  return (
    <>
      {canManage ? (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="btn-primary"
          >
            New campaign
          </button>
        </div>
      ) : null}

      <Card flush>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner label="Loading campaigns…" />
          </div>
        ) : isError ? (
          <EmptyState title="Couldn't load campaigns" description={(error as Error)?.message} />
        ) : (
          <>
            <DataTable<Campaign>
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(r) => String(r.id)}
              stickyHeader
              empty={<EmptyState title="No campaigns" description="No incentive campaigns yet." icon={<IconAward className="h-6 w-6" />} />}
            />
            <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
          </>
        )}
      </Card>

      {editing ? (
        <CampaignDrawer campaign={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
}

// ─── campaign authoring drawer ───────────────────────────────

/** The kinds incentives-svc accepts. 'trip_count' is v1 (do N trips in the
 *  window); 'streak' is reserved (roadmap on the consumer) but authorable. */
const KIND_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'trip_count', label: 'Trip count (do N trips in the window)' },
  { value: 'streak', label: 'Streak (consecutive accepts) — roadmap' },
];

/** The editable form state. Mirrors CampaignInput but keeps the optional/number
 *  fields as the strings the inputs bind to (parsed on submit). trip_type is a
 *  string: '' means "any trip type" (→ null). */
interface FormState {
  code: string;
  title: string;
  description: string;
  kind: string;
  target_count: string;
  reward_amount: string;
  trip_type: string;
  window_start: string; // datetime-local value
  window_end: string; // datetime-local value
  active: boolean;
}

/** CampaignDrawer — the create/edit form. campaign=null ⇒ create; otherwise
 *  edit that row (the form is seeded from it). On success it invalidates the
 *  campaigns list (so the table reflects the change) and closes.
 *
 *  RBAC: only ops + superadmin hold incentives.manage; this drawer only ever
 *  opens for them (the page gates the New/Edit affordances on the same hook).
 *  admin-svc enforces the matrix server-side and 403s regardless. */
function CampaignDrawer({ campaign, onClose }: { campaign: Campaign | null; onClose: () => void }) {
  const isEdit = campaign !== null;
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(() => seedForm(campaign));
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: CampaignInput) =>
      isEdit ? adminApi.updateCampaign(campaign!.id, input) : adminApi.createCampaign(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'campaigns'] });
      onClose();
    },
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = () => {
    setClientError(null);
    const built = buildInput(form);
    if ('error' in built) {
      setClientError(built.error);
      return;
    }
    mutation.mutate(built.input);
  };

  return (
    <Drawer
      title={isEdit ? `Edit campaign #${campaign!.id}` : 'New campaign'}
      subtitle={isEdit ? campaign!.code : 'Author a driver quest / incentive'}
      onClose={onClose}
    >
      <div className="space-y-4">
        {isEdit ? (
          <div>
            <Field label="ID" value={`#${campaign!.id}`} />
            <Field label="Created" value={dateTime(campaign!.created_at)} />
          </div>
        ) : null}

        <Card>
          <div className="space-y-3">
            <TextField
              label="Code"
              hint="Unique handle (e.g. WEEKLY_20). Used to key progress; must be unique."
              value={form.code}
              onChange={(v) => set('code', v)}
              placeholder="WEEKLY_20"
            />
            <TextField
              label="Title"
              hint="Shown to drivers on the Quests screen."
              value={form.title}
              onChange={(v) => set('title', v)}
              placeholder="Complete 20 trips this week"
            />
            <div>
              <label className="block text-[11px] text-muted">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                rows={2}
                placeholder="Earn a $50 bonus when you complete 20 trips between Monday and Sunday."
                className="mt-1 input"
              />
            </div>

            <div>
              <label className="block text-[11px] text-muted">Kind</label>
              <select
                value={form.kind}
                onChange={(e) => set('kind', e.target.value)}
                className="mt-1 input"
              >
                {KIND_OPTIONS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Target count"
                hint="Qualifying trips to complete."
                value={form.target_count}
                onChange={(v) => set('target_count', v)}
                type="number"
                placeholder="20"
              />
              <TextField
                label="Reward (AUD)"
                hint="Bonus paid on completion."
                value={form.reward_amount}
                onChange={(v) => set('reward_amount', v)}
                type="number"
                placeholder="50.00"
              />
            </div>

            <TextField
              label="Trip type (optional)"
              hint="Restrict to one trip-type id (e.g. 1 = Premier). Leave blank for any."
              value={form.trip_type}
              onChange={(v) => set('trip_type', v)}
              type="number"
              placeholder="any"
            />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] text-muted">Window start</label>
                <input
                  type="datetime-local"
                  value={form.window_start}
                  onChange={(e) => set('window_start', e.target.value)}
                  className="mt-1 input"
                />
              </div>
              <div>
                <label className="block text-[11px] text-muted">Window end</label>
                <input
                  type="datetime-local"
                  value={form.window_end}
                  onChange={(e) => set('window_end', e.target.value)}
                  className="mt-1 input"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => set('active', e.target.checked)}
                className="h-4 w-4 rounded border-ink-700 bg-ink-850 text-brand-500 focus:ring-brand-500"
              />
              Active (drivers can see + progress this quest)
            </label>
          </div>
        </Card>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={onSubmit}
            className="btn-primary"
          >
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create campaign'}
          </button>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={onClose}
            className="btn-secondary"
          >
            Cancel
          </button>
          {clientError ? <span className="text-xs text-danger">{clientError}</span> : null}
          {mutation.isError ? <span className="text-xs text-danger">{(mutation.error as Error)?.message}</span> : null}
        </div>
      </div>
    </Drawer>
  );
}

/** A labelled text/number input row used in the campaign form. */
function TextField({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 input"
      />
      {hint ? <p className="mt-0.5 text-[11px] text-muted">{hint}</p> : null}
    </div>
  );
}

/** Seed the form from a campaign (edit) or empty defaults (create). Window
 *  bounds become datetime-local strings; trip_type null becomes ''. */
function seedForm(c: Campaign | null): FormState {
  if (!c) {
    return {
      code: '',
      title: '',
      description: '',
      kind: 'trip_count',
      target_count: '',
      reward_amount: '',
      trip_type: '',
      window_start: '',
      window_end: '',
      active: true,
    };
  }
  return {
    code: c.code,
    title: c.title,
    description: c.description ?? '',
    kind: c.kind || 'trip_count',
    target_count: String(c.target_count),
    reward_amount: String(c.reward_amount),
    trip_type: c.trip_type === null || c.trip_type === undefined ? '' : String(c.trip_type),
    window_start: isoToLocalInput(c.window_start),
    window_end: isoToLocalInput(c.window_end),
    active: c.active,
  };
}

/** Validate + convert the form into a CampaignInput, or return an error string.
 *  Mirrors admin-svc/incentives-svc validation so the staffer sees a precise
 *  message instantly; the server re-checks everything (this is a UX nicety, not
 *  the boundary). Window bounds are converted local-input → RFC3339 (UTC). */
function buildInput(f: FormState): { input: CampaignInput } | { error: string } {
  const code = f.code.trim();
  if (!code) return { error: 'Code is required.' };
  const title = f.title.trim();
  if (!title) return { error: 'Title is required.' };

  const target = Number(f.target_count);
  if (!Number.isInteger(target) || target <= 0) return { error: 'Target count must be a positive whole number.' };

  const reward = Number(f.reward_amount);
  if (Number.isNaN(reward) || reward < 0) return { error: 'Reward must be a number of zero or more.' };

  let tripType: number | null = null;
  if (f.trip_type.trim() !== '') {
    const tt = Number(f.trip_type);
    if (!Number.isInteger(tt) || tt < 0) return { error: 'Trip type must be a non-negative whole number, or blank for any.' };
    tripType = tt;
  }

  if (!f.window_start || !f.window_end) return { error: 'Window start and end are both required.' };
  const startISO = localInputToISO(f.window_start);
  const endISO = localInputToISO(f.window_end);
  if (!startISO || !endISO) return { error: 'Window start/end are not valid dates.' };
  if (new Date(endISO).getTime() <= new Date(startISO).getTime()) {
    return { error: 'Window end must be after window start.' };
  }

  return {
    input: {
      code,
      title,
      description: f.description.trim(),
      kind: f.kind || 'trip_count',
      target_count: target,
      reward_amount: reward,
      trip_type: tripType,
      window_start: startISO,
      window_end: endISO,
      active: f.active,
    },
  };
}

/** RFC3339/ISO (UTC) → the local-time string <input type="datetime-local">
 *  expects (YYYY-MM-DDTHH:mm in the browser's zone). */
function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Shift by the zone offset so toISOString's slice reflects LOCAL wall-clock.
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

/** A datetime-local value (local wall-clock) → RFC3339 (UTC, with Z), the shape
 *  incentives-svc parses. Empty/invalid input → ''. */
function localInputToISO(local: string): string {
  if (!local) return '';
  const d = new Date(local); // interpreted as local time by the JS engine
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
}

const TIER_TONE: Record<string, BadgeTone> = {
  Diamond: 'info',
  Platinum: 'ok',
  Gold: 'warn',
  Blue: 'neutral',
};

/** Driver loyalty leaderboard (driver_loyalty_points, current period). */
function TiersTable() {
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'tiers', offset],
    queryFn: ({ signal }) => adminApi.driverTiers({ limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<DriverTier>> = [
    { key: 'driver', header: 'Driver', cell: (r) => <span className="metric text-sm font-medium text-slate-100">#{r.driver_id}</span> },
    { key: 'tier', header: 'Tier', cell: (r) => <Badge tone={TIER_TONE[r.tier] ?? 'neutral'}>{r.tier}</Badge> },
    { key: 'points', header: 'Points', align: 'right', cell: (r) => <span className="metric text-sm text-slate-200">{r.points}</span> },
    { key: 'trips', header: 'Trips', align: 'right', cell: (r) => <span className="metric text-xs text-slate-300">{r.trips}</span> },
    { key: 'period', header: 'Period', cell: (r) => <span className="text-xs text-muted">{dateTime(r.period_start)}</span> },
    { key: 'updated', header: 'Updated', cell: (r) => <span className="text-xs text-muted">{dateTime(r.updated_at)}</span> },
  ];

  const unavailable = data && data.available === false;
  if (unavailable) {
    return (
      <Card>
        <EmptyState
          title="Loyalty tiers not yet in Postgres"
          description={data?.note ?? 'The driver_loyalty_points table is owned by loyalty-svc and is not in this Postgres yet.'}
          icon={<IconAward className="h-6 w-6" />}
        />
      </Card>
    );
  }
  return (
    <Card flush>
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner label="Loading driver tiers…" />
        </div>
      ) : isError ? (
        <EmptyState title="Couldn't load driver tiers" description={(error as Error)?.message} />
      ) : (
        <>
          <DataTable<DriverTier>
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(r) => String(r.driver_id)}
            stickyHeader
            empty={<EmptyState title="No tier data" description="No driver loyalty points recorded for the current period yet." icon={<IconAward className="h-6 w-6" />} />}
          />
          <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
        </>
      )}
    </Card>
  );
}
